import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingDown, TrendingUp } from "lucide-react";
import { PortalShell } from "./PortalShell";
import { portalApi, portalErr } from "./portalApi";
import "../test/testTheme.css";

const Bar = ({ pct }) => (
  <div style={{ flex: 1, background: "#eef1f5", borderRadius: 6, height: 10, overflow: "hidden" }}>
    <div style={{ width: `${pct}%`, height: "100%", borderRadius: 6, background: pct >= 70 ? "#12b76a" : pct >= 50 ? "#f7b500" : "#d92d20" }} />
  </div>
);

function WeaknessInner() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    portalApi.get("/portal/weakness").then(({ data: d }) => setData(d)).catch((err) => setError(portalErr(err)));
  }, []);

  return (
    <div className="tp-wrap" style={{ maxWidth: 820, padding: "44px 22px 70px" }}>
      <div className="tp-eyebrow">FIND YOUR PHYSICS WEAKNESS</div>
      <h1 className="tp-h2" style={{ fontSize: 32 }} data-testid="weakness-title">Know exactly what to revise next</h1>
      {error && <p className="tp-error" role="alert" data-testid="weakness-error">{error}</p>}
      {!data && !error && <p className="tp-muted">Analysing your quiz records…</p>}
      {data && !data.enough_data && (
        <div className="tp-panel" style={{ textAlign: "center" }} data-testid="weakness-insufficient">
          <p className="tp-muted">{data.message}</p>
          <Link className="tp-btn" to="/portal/quiz" data-testid="weakness-start-quiz">TAKE A QUIZ <ArrowRight size={16} /></Link>
        </div>
      )}
      {data?.enough_data && <>
        <div className="tp-cards" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", margin: "20px 0" }} data-testid="weakness-stats">
          <div className="tp-card"><h3>{data.overall_accuracy}%</h3><p>Overall accuracy</p></div>
          <div className="tp-card"><h3>{data.total_questions}</h3><p>Questions attempted</p></div>
          <div className="tp-card"><h3>{data.correct}</h3><p>Correct answers</p></div>
          <div className="tp-card"><h3>{data.trend ? `${data.trend.recent}%` : "—"}</h3><p>Recent accuracy {data.trend ? `(${data.trend.direction})` : "(need 6 quizzes)"}</p></div>
        </div>
        {data.recommendations.length > 0 && (
          <section className="tp-panel" style={{ borderLeft: "4px solid #f77f00" }} data-testid="weakness-recommendations">
            <h2 className="tp-h2" style={{ fontSize: 18 }}>Recommended revision plan</h2>
            {data.recommendations.map((r, i) => <p key={i} className="tp-muted" style={{ margin: "8px 0" }} data-testid={`weakness-rec-${i}`}>• {r}</p>)}
            <p className="tp-note" style={{ marginTop: 10 }}>Based on your actual quiz and test records — not guesswork.</p>
          </section>
        )}
        <section className="tp-panel" style={{ marginTop: 18 }} data-testid="weakness-chapters">
          <h2 className="tp-h2" style={{ fontSize: 18 }}>Chapter-wise accuracy</h2>
          {data.topics.map((t, i) => (
            <div key={t.name} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #eef1f5" }} data-testid={`weakness-topic-${i}`}>
              <span style={{ width: 200, fontWeight: 600, fontSize: 14 }}>{t.name}{t.total < 3 && <small className="tp-note" style={{ display: "block" }}>attempt more for a verdict</small>}</span>
              <Bar pct={t.accuracy} />
              <b style={{ width: 46, textAlign: "right", fontSize: 13 }}>{t.accuracy}%</b>
              <span className="tp-note" style={{ width: 60 }}>{t.correct}/{t.total}</span>
            </div>
          ))}
          <div style={{ display: "flex", gap: 18, marginTop: 14, flexWrap: "wrap" }}>
            {data.strong_topics.length > 0 && <span className="tp-pill ok" data-testid="weakness-strong"><TrendingUp size={13} /> Strong: {data.strong_topics.map((t) => t.name).join(", ")}</span>}
            {data.weak_topics.length > 0 && <span className="tp-pill bad" data-testid="weakness-weak"><TrendingDown size={13} /> Weak: {data.weak_topics.map((t) => t.name).join(", ")}</span>}
          </div>
        </section>
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <Link className="tp-btn" to="/portal/quiz" data-testid="weakness-practice">PRACTISE A WEAK CHAPTER <ArrowRight size={16} /></Link>
        </div>
      </>}
    </div>
  );
}

export default function PortalWeakness() {
  return <PortalShell><WeaknessInner /></PortalShell>;
}
